import { createServerFn } from "@tanstack/react-start";
import { Result } from "better-result";
import { renderSVG } from "uqr";
import { z } from "zod/v4";
import { m } from "#/paraglide/messages";
import { client } from "#/modules/util.fetch-client/fetch-client.server";
import { toResult } from "#/modules/util.auth/api-errors";
import { authMiddleware } from "#/modules/util.auth/middleware.auth";
import { getAuthSession } from "#/modules/util.auth/session.auth.server";
import { getUserInfo } from "#/modules/util.auth/api.auth.server";
import { loginCallback } from "#/modules/util.auth/authenticate.server";
import { genericFormError, translateApiError } from "#/modules/util.errors/translate";
import { setSentryUser } from "#/modules/util.instrumentation/sentry";
// Recovery login happens while locked out — the ticket flow is unreachable, so point at email.
const lockedOutError = () => m["common.something_went_wrong_email"]();

/**
 * GET /auth/user/2fa/setup — sharedKey + a scannable QR image (authenticated).
 *
 * Mirrors portal's `_protected.setup-2fa` loader: the `otpauth://` URI from the API is
 * rendered to an SVG QR with `uqr` and returned as a base64 data URL, so the client can
 * drop it straight into an `<img src>` (rendering happens server-side; `Buffer` is available).
 * A `TwoFactorAlreadyEnabled` API error becomes a dedicated `alreadyEnabled` flag the page
 * renders as its "already enabled" screen instead of a generic error.
 */
export const get2FASetup = createServerFn({ method: "GET" })
  .middleware([authMiddleware])
  .handler(async () => {
    const result = toResult(await client.GET("/auth/user/2fa/setup"));
    if (Result.isError(result)) {
      if (result.error.title === "TwoFactorAlreadyEnabled") {
        return { alreadyEnabled: true as const };
      }
      return { formError: translateApiError(result.error) };
    }
    const data = result.value;
    if (!data) return genericFormError("TwoFactorSetupBodyMissing");

    let qrCodeDataUrl: string | null = null;
    if (data.qrCodeUri) {
      try {
        const svg = renderSVG(data.qrCodeUri, {
          border: 2,
          blackColor: "#000",
          whiteColor: "#fff",
        });
        qrCodeDataUrl = `data:image/svg+xml;base64,${Buffer.from(svg).toString("base64")}`;
      } catch (err) {
        console.error("Failed to generate QR code:", err);
      }
    }

    return {
      sharedKey: data.sharedKey,
      qrCodeDataUrl,
    };
  });

/** POST /auth/user/2fa/verify — enable 2FA with a TOTP code (authenticated). */
export const verify2FASetup = createServerFn({ method: "POST" })
  .middleware([authMiddleware])
  .validator(z.object({ code: z.string().min(6).max(6) }))
  .handler(async ({ data }) => {
    const result = toResult(
      await client.POST("/auth/user/2fa/verify", {
        body: { code: data.code },
      }),
    );
    if (Result.isError(result)) return { formError: translateApiError(result.error) };
    if (!result.value) return genericFormError("TwoFactorVerifyBodyMissing");
    return { recoveryCodes: result.value.recoveryCodes };
  });

/** POST /auth/user/2fa/disable — disable 2FA (requires current password). */
export const disable2FA = createServerFn({ method: "POST" })
  .middleware([authMiddleware])
  .validator(z.object({ password: z.string().min(1) }))
  .handler(async ({ data }) => {
    const result = toResult(
      await client.POST("/auth/user/2fa/disable", {
        body: { password: data.password },
      }),
    );
    if (Result.isError(result)) return { formError: translateApiError(result.error) };
  });

/** POST /auth/user/2fa/recover — disable 2FA using a recovery code (authenticated). */
export const recover2FA = createServerFn({ method: "POST" })
  .middleware([authMiddleware])
  .validator(z.object({ code: z.string().min(1) }))
  .handler(async ({ data }) => {
    const result = toResult(
      await client.POST("/auth/user/2fa/recover", {
        body: { code: data.code },
      }),
    );
    if (Result.isError(result)) return { formError: translateApiError(result.error) };
  });

/**
 * POST /auth/login/2fa/recover — complete login with a recovery code (unauthenticated).
 * Uses the temporary token stashed in the session during the password step.
 */
export const loginWith2FARecovery = createServerFn({ method: "POST" })
  .validator(z.object({ email: z.string().email(), recoveryCode: z.string().min(1) }))
  .handler(async ({ data }): Promise<{ formError: string } | undefined> => {
    setSentryUser(data.email);
    const session = await getAuthSession();
    const temporaryToken = session.data.tokens?.temporaryToken;
    if (!temporaryToken) return { formError: lockedOutError() };

    const tokensResult = toResult(
      await client.POST("/auth/login/2fa/recover", {
        body: { email: data.email, temporaryToken, recoveryCode: data.recoveryCode },
      }),
    );
    if (Result.isError(tokensResult)) return { formError: translateApiError(tokensResult.error) };
    const tokens = tokensResult.value;
    if (!tokens?.accessToken) return { formError: lockedOutError() };

    const userResult = await getUserInfo(tokens.accessToken);
    if (Result.isError(userResult)) return { formError: translateApiError(userResult.error) };

    await session.update({ tokens, user: userResult.value });
    await loginCallback(); // throws a redirect
  });
